/**
 * The Markdown a member writes in a chat reply, read into a small tree that ZhMarkdown draws. A subset, on purpose:
 * paragraphs (a single newline stays a line break), headings, bulleted and numbered lists, fenced code, rules, and
 * inline bold, italics, code and links, a bare web address included. Anything else stays the text it was. The tree holds strings and never markup,
 * so raw HTML in a message is shown as the characters it was written in and can never become an element.
 *
 * Only `http:`, `https:` and `mailto:` destinations become links. Any other one (`javascript:`, `data:`, a relative
 * path) leaves the whole link as it was written, and image syntax becomes a link to the image: a message never loads
 * a remote resource.
 */

export type MarkdownInline =
    | { kind: 'text'; text: string }
    | { kind: 'break' }
    | { kind: 'strong'; children: MarkdownInline[] }
    | { kind: 'em'; children: MarkdownInline[] }
    | { kind: 'code'; text: string }
    | { kind: 'link'; href: string; children: MarkdownInline[] }

export type MarkdownBlock =
    | { kind: 'paragraph'; children: MarkdownInline[] }
    | { kind: 'heading'; children: MarkdownInline[] }
    | { kind: 'code'; text: string }
    | { kind: 'rule' }
    | { kind: 'list'; ordered: boolean; start: number; items: MarkdownBlock[][] }

/** An open code fence: what it was opened with, which only the same character, as many times or more, closes. */
export interface MarkdownFence {
    char: string
    length: number
}

const HEADING = /^ {0,3}(#{1,6})(?:[ \t]+(.*?))?(?:[ \t]+#+)?[ \t]*$/
const RULE = /^ {0,3}([-*_])(?:[ \t]*\1){2,}[ \t]*$/
const BULLET = /^( {0,3})([-*+])[ \t]+(.*)$/
const ORDERED = /^( {0,3})(\d{1,9})[.)][ \t]+(.*)$/
const PUNCTUATION = /^[!-/:-@[-`{-~]$/
const WORD = /^[\p{L}\p{N}]$/u
const SPACE = /^\s$/
const SAFE_SCHEME = /^(?:https?:|mailto:)/i
const UNSAFE_CHARACTER = /[\s\p{Cc}]/u
/** A link's title, after its destination (`"…"`, `'…'` or `(…)`): allowed, and not drawn. */
const LINK_TITLE = /^(?:".*"|'.*'|\(.*\))$/s
/** A web address written out in the text, up to the next space or angle bracket. Sticky: read from `lastIndex`. */
const BARE_URL = /https?:\/\/[^\s<>]+/iy
/** An address in angle brackets (`<https://…>`), which is a link without the brackets. Sticky, as above. */
const ANGLE_URL = /<((?:https?|mailto):[^\s<>]+)>/iy
/** What may end a sentence after an address rather than belong to it. */
const TRAILING = /^[.,:;!?'"*_~]$/

/** The fence a line opens, if it opens one. A backtick fence's info string may not hold a backtick. */
export function openFence(line: string): MarkdownFence | null {
    const match = /^ {0,3}(`{3,}|~{3,})(.*)$/.exec(line)
    const run = match?.[1]
    if (!run || (run.startsWith('`') && match[2]?.includes('`'))) {
        return null
    }
    return { char: run.charAt(0), length: run.length }
}

/** Whether a line closes the fence that is open. */
export function closesFence(line: string, fence: MarkdownFence): boolean {
    const run = /^ {0,3}(`{3,}|~{3,})[ \t]*$/.exec(line)?.[1]
    return run !== undefined && run.charAt(0) === fence.char && run.length >= fence.length
}

/** A destination a link may go to, else null: `http:`, `https:` or `mailto:`, with no space or control character. */
export function safeHref(destination: string): string | null {
    return SAFE_SCHEME.test(destination) && !UNSAFE_CHARACTER.test(destination) ? destination : null
}

/** A web address written bare at `at` (`https://…`), as a link to it, or null. */
export interface BareUrl {
    href: string
    end: number
}

/**
 * The web address written bare at `at`, or null. It starts a word and runs to the next space. Punctuation that ends
 * the sentence is left off, and so is a closing bracket the address never opened: "(see https://x.test/a)." links
 * `https://x.test/a`.
 */
export function bareUrlAt(text: string, at: number): BareUrl | null {
    if (WORD.test(text.charAt(at - 1))) {
        return null
    }
    BARE_URL.lastIndex = at
    let url = BARE_URL.exec(text)?.[0] ?? ''
    while (url && unbalanced(url)) {
        url = url.slice(0, -1)
    }
    const href = /^https?:\/\/[\p{L}\p{N}]/iu.test(url) ? safeHref(url) : null
    return href ? { href, end: at + href.length } : null
}

/** Whether an address's last character belongs to the sentence after it, not to the address. */
function unbalanced(url: string): boolean {
    const last = url.charAt(url.length - 1)
    const opens = { ')': '(', ']': '[' }[last]
    return TRAILING.test(last) || (opens !== undefined && url.split(opens).length < url.split(last).length)
}

/** A run of a person's text as they typed it: text, or a web address in it as a link. */
export type TypedPart = { kind: 'text'; text: string } | { kind: 'link'; href: string }

/** What a person typed, with each web address in it as a link. Nothing else is read: no Markdown, no brackets. */
export function linkTyped(text: string): TypedPart[] {
    const parts: TypedPart[] = []
    let plain = ''
    let at = 0
    while (at < text.length) {
        const url = bareUrlAt(text, at)
        if (url) {
            if (plain) {
                parts.push({ kind: 'text', text: plain })
                plain = ''
            }
            parts.push({ kind: 'link', href: url.href })
            at = url.end
        } else {
            plain += text.charAt(at)
            at += 1
        }
    }
    if (plain) {
        parts.push({ kind: 'text', text: plain })
    }
    return parts
}

/** How deep lists may sit inside lists; an item deeper than this reads as the text it is. */
const DEEPEST_LIST = 8

/** How deep emphasis may sit inside emphasis; stars deeper than this read as written, so no reply runs out of stack. */
const DEEPEST_EMPHASIS = 64

/** A whole reply, as blocks. */
export function parseMarkdown(text: string): MarkdownBlock[] {
    return new BlockReader(text.split(/\r\n?|\n/), 0).read()
}

/** A line of text, as inline nodes. `links` is false inside a link's own text: a link never holds another. */
export function parseInline(text: string, links = true): MarkdownInline[] {
    return new InlineReader(text, links).read()
}

interface ListMarker {
    ordered: boolean
    start: number
    /** Spaces before the marker. */
    lead: number
    /** The column the item's text starts at: lines indented this far belong to the item. */
    indent: number
    content: string
}

function listMarker(line: string): ListMarker | null {
    const match = BULLET.exec(line) ?? ORDERED.exec(line)
    if (!match) {
        return null
    }
    const content = match[3] ?? ''
    const ordered = /\d/.test(match[2] ?? '')
    return {
        ordered,
        start: ordered ? Number(match[2]) : 1,
        lead: (match[1] ?? '').length,
        indent: line.length - content.length,
        content,
    }
}

function isBlank(line: string): boolean {
    return line.trim() === ''
}

/** How far a line is indented, a tab counting as four spaces. */
function leadOf(line: string): number {
    return (/^[ \t]*/.exec(line)?.[0] ?? '').replace(/\t/g, '    ').length
}

/** A line with up to `width` columns of its indent taken off, so an item's own lines read as a reply of their own. */
function dedent(line: string, width: number): string {
    const lead = /^[ \t]*/.exec(line)?.[0] ?? ''
    const spaces = lead.replace(/\t/g, '    ')
    return spaces.slice(Math.min(width, spaces.length)) + line.slice(lead.length)
}

/** Whether a line starts a block of its own, and so cannot carry on the text above it. */
function startsBlock(line: string): boolean {
    return openFence(line) !== null || HEADING.test(line) || RULE.test(line) || listMarker(line) !== null
}

/** Reads lines into blocks, one block at a time; each step takes the block at the current line, if it is its kind. */
class BlockReader {
    private readonly lines: readonly string[]
    /** How many lists these lines sit inside. */
    private readonly depth: number
    private readonly blocks: MarkdownBlock[] = []
    private paragraph: string[] = []
    private at = 0

    constructor(lines: readonly string[], depth: number) {
        this.lines = lines
        this.depth = depth
    }

    read(): MarkdownBlock[] {
        while (this.at < this.lines.length) {
            const line = this.lines[this.at] ?? ''
            if (!(this.blank(line) || this.fence(line) || this.heading(line) || this.rule(line) || this.list(line))) {
                this.paragraph.push(line)
                this.at += 1
            }
        }
        this.endParagraph()
        return this.blocks
    }

    private endParagraph(): void {
        if (this.paragraph.length > 0) {
            const text = this.paragraph.map(line => line.trim()).join('\n')
            this.blocks.push({ kind: 'paragraph', children: parseInline(text) })
            this.paragraph = []
        }
    }

    /** Takes one whole-line block: ends the paragraph above it and moves past the line. */
    private take(block: MarkdownBlock | null): true {
        this.endParagraph()
        if (block) {
            this.blocks.push(block)
        }
        this.at += 1
        return true
    }

    private blank(line: string): boolean {
        return isBlank(line) && this.take(null)
    }

    private fence(line: string): boolean {
        const fence = openFence(line)
        if (!fence) {
            return false
        }
        const code: string[] = []
        let at = this.at + 1
        while (at < this.lines.length && !closesFence(this.lines[at] ?? '', fence)) {
            code.push(this.lines[at] ?? '')
            at += 1
        }
        // The closing fence is taken with the block; a fence left open runs to the end of the reply.
        this.at = at
        return this.take({ kind: 'code', text: code.join('\n') })
    }

    private heading(line: string): boolean {
        const heading = HEADING.exec(line)
        if (!heading) {
            return false
        }
        const children = parseInline(heading[2] ?? '')
        return this.take(children.length > 0 ? { kind: 'heading', children } : null)
    }

    private rule(line: string): boolean {
        return RULE.test(line) && this.take({ kind: 'rule' })
    }

    private list(line: string): boolean {
        const marker = this.depth < DEEPEST_LIST ? listMarker(line) : null
        // A numbered line inside a paragraph ("2026. A year") only starts a list when it starts at 1.
        if (!marker || (this.paragraph.length > 0 && marker.ordered && marker.start !== 1)) {
            return false
        }
        this.endParagraph()
        const list = new ListReader(this.lines, this.at, marker, this.depth + 1)
        this.blocks.push(list.read())
        this.at = list.next
        return true
    }
}

/**
 * One list from its first item: the items after it of the same kind, each with the lines that belong to it (indented
 * under it, or carrying its text straight on). Each item's lines are read as blocks of their own, so a list indented
 * under an item is a list inside it.
 */
class ListReader {
    private readonly lines: readonly string[]
    private readonly first: ListMarker
    /** How many lists this one's items sit inside, itself included. */
    private readonly depth: number
    private readonly items: string[][] = []
    private item: string[]
    private indent: number
    private afterBlank = false
    /** The first line after the list, once it is read. */
    next: number

    constructor(lines: readonly string[], from: number, first: ListMarker, depth: number) {
        this.lines = lines
        this.first = first
        this.depth = depth
        this.item = [first.content]
        this.indent = first.indent
        this.next = from + 1
    }

    read(): MarkdownBlock {
        while (this.next < this.lines.length && this.takes(this.lines[this.next] ?? '')) {
            this.next += 1
        }
        this.items.push(this.item)
        return {
            kind: 'list',
            ordered: this.first.ordered,
            start: this.first.start,
            items: this.items.map(itemLines => new BlockReader(itemLines, this.depth).read()),
        }
    }

    /** Whether the line belongs to the list: a blank, the next item, or a line of the current one. */
    private takes(line: string): boolean {
        if (isBlank(line)) {
            this.item.push('')
            this.afterBlank = true
            return true
        }
        const marker = listMarker(line)
        if (marker && marker.ordered === this.first.ordered && marker.lead < this.indent && !RULE.test(line)) {
            this.items.push(this.item)
            this.item = [marker.content]
            this.indent = marker.indent
        } else if (leadOf(line) >= 2) {
            this.item.push(dedent(line, this.indent))
        } else if (!this.afterBlank && !startsBlock(line)) {
            this.item.push(line)
        } else {
            return false
        }
        this.afterBlank = false
        return true
    }
}

/** How many times `char` repeats from `at`. */
function runOf(text: string, at: number, char: string): number {
    let end = at
    while (text.charAt(end) === char) {
        end += 1
    }
    return end - at
}

function isSpace(char: string): boolean {
    return char === '' || SPACE.test(char)
}

/** A `*` or `_` run can open emphasis when text follows it; `_` not inside a word (`snake_case_name`). */
function canOpen(text: string, at: number, run: number, char: string): boolean {
    return !isSpace(text.charAt(at + run)) && (char !== '_' || !WORD.test(text.charAt(at - 1)))
}

/** A run can close emphasis when text comes before it; `_` not inside a word. */
function canClose(text: string, at: number, run: number, char: string): boolean {
    return !isSpace(text.charAt(at - 1)) && (char !== '_' || !WORD.test(text.charAt(at + run)))
}

/** A code span opened at `at`: up to the next run of exactly as many backticks. */
function codeSpan(text: string, at: number): { text: string; end: number } | null {
    const run = runOf(text, at, '`')
    let scan = text.indexOf('`', at + run)
    while (scan >= 0) {
        const length = runOf(text, scan, '`')
        if (length === run) {
            const body = text.slice(at + run, scan).replace(/\n/g, ' ')
            const padded = body.startsWith(' ') && body.endsWith(' ') && body.trim() !== ''
            return { text: padded ? body.slice(1, -1) : body, end: scan + length }
        }
        scan = text.indexOf('`', scan + length)
    }
    return null
}

/** The index of the bracket that closes the one at `at`, or -1. A destination's brackets stay on one line. */
function matching(text: string, at: number, open: string, close: string): number {
    let depth = 0
    for (let scan = at; scan < text.length; scan += 1) {
        const char = text.charAt(scan)
        if (char === '\\') {
            scan += 1
        } else if (char === open) {
            depth += 1
        } else if (char === close) {
            depth -= 1
            if (depth === 0) {
                return scan
            }
        } else if (char === '\n' && open === '(') {
            return -1
        }
    }
    return -1
}

interface Link {
    image: boolean
    label: string
    href: string | null
    raw: string
    end: number
}

/** A link (`[text](url)`) or an image (`![alt](url)`) starting at `at`, safe or not, or null when there is none. */
function linkAt(text: string, at: number): Link | null {
    const image = text.charAt(at) === '!'
    const open = image ? at + 1 : at
    const labelEnd = matching(text, open, '[', ']')
    const end = labelEnd >= 0 && text.charAt(labelEnd + 1) === '(' ? matching(text, labelEnd + 1, '(', ')') : -1
    if (end < 0) {
        return null
    }
    const [destination = '', ...title] = text
        .slice(labelEnd + 2, end)
        .trim()
        .split(/\s+/)
    if (title.length > 0 && !LINK_TITLE.test(title.join(' '))) {
        return null
    }
    return {
        image,
        label: text.slice(open + 1, labelEnd),
        href: safeHref(destination.replace(/^<(.*)>$/, '$1')),
        raw: text.slice(at, end + 1),
        end: end + 1,
    }
}

/** Emphasis a run opens: `size` delimiters (1 italic, 2 bold, 3 both) around the words from `from` to `to`. */
type Emphasis = { size: number; from: number; to: number; end: number }

/**
 * Reads a run of text into inline nodes, left to right; each step takes what starts at the current character, if it
 * is its kind, and anything no step takes is text.
 */
class InlineReader {
    private readonly text: string
    private readonly links: boolean
    /** How many emphases this text sits inside. */
    private readonly depth: number
    private readonly nodes: MarkdownInline[] = []
    private plain = ''
    private at = 0
    /** Emphasis opened at each index, worked out once: an unclosed `*` is looked past by every opener before it. */
    private readonly opened = new Map<number, Emphasis | null>()

    constructor(text: string, links: boolean, depth = 0) {
        this.text = text
        this.links = links
        this.depth = depth
    }

    read(): MarkdownInline[] {
        // Emphasis is worked out from the end back, so every opener finds the ones after it already known: a long
        // run of openers that never close costs a scan each, never a call stack as deep as the run.
        for (let at = this.text.length - 1; at >= 0; at -= 1) {
            const char = this.text.charAt(at)
            if ((char === '*' || char === '_') && this.text.charAt(at - 1) !== char) {
                this.emphasisAt(at)
            }
        }
        while (this.at < this.text.length) {
            if (
                !(this.escape() || this.lineBreak() || this.code() || this.link() || this.address() || this.emphasis())
            ) {
                this.plain += this.text.charAt(this.at)
                this.at += 1
            }
        }
        this.endText()
        return this.nodes
    }

    private endText(): void {
        if (this.plain) {
            this.nodes.push({ kind: 'text', text: this.plain })
            this.plain = ''
        }
    }

    private push(node: MarkdownInline, end: number): true {
        this.endText()
        this.nodes.push(node)
        this.at = end
        return true
    }

    /** Characters taken as they are, written out: an escaped one, or a run that did not open or close anything. */
    private literal(text: string): true {
        this.plain += text
        this.at += text.length
        return true
    }

    /** Whether the text at the current character reads `chars`. */
    private sees(chars: string): boolean {
        return this.text.startsWith(chars, this.at)
    }

    private escape(): boolean {
        const escaped = this.text.charAt(this.at + 1)
        if (!this.sees('\\') || !PUNCTUATION.test(escaped)) {
            return false
        }
        this.literal(escaped)
        this.at += 1
        return true
    }

    private lineBreak(): boolean {
        return this.sees('\n') && this.push({ kind: 'break' }, this.at + 1)
    }

    private code(): boolean {
        if (!this.sees('`')) {
            return false
        }
        const span = codeSpan(this.text, this.at)
        return span
            ? this.push({ kind: 'code', text: span.text }, span.end)
            : this.literal('`'.repeat(runOf(this.text, this.at, '`')))
    }

    private link(): boolean {
        const starts = this.sees('[') || this.sees('![')
        const link = this.links && starts ? linkAt(this.text, this.at) : null
        if (!link) {
            return false
        }
        if (link.href === null) {
            // Not a destination a message may link to: what was written, as it was written.
            return this.literal(link.raw)
        }
        const label = link.image ? [] : new InlineReader(link.label, false, this.depth).read()
        const children: MarkdownInline[] = label.length > 0 ? label : [{ kind: 'text', text: link.label || link.href }]
        return this.push({ kind: 'link', href: link.href, children }, link.end)
    }

    /** A web address written out, bare or in angle brackets, as a link to itself. Never inside a link's own words. */
    private address(): boolean {
        if (!this.links) {
            return false
        }
        ANGLE_URL.lastIndex = this.at
        const angled = ANGLE_URL.exec(this.text)
        const href = angled?.[1] ? safeHref(angled[1]) : null
        const url = angled && href ? { href, end: this.at + angled[0].length } : bareUrlAt(this.text, this.at)
        return (
            url !== null &&
            this.push({ kind: 'link', href: url.href, children: [{ kind: 'text', text: url.href }] }, url.end)
        )
    }

    private emphasis(): boolean {
        const char = this.text.charAt(this.at)
        if (char !== '*' && char !== '_') {
            return false
        }
        const found = this.emphasisAt(this.at)
        return found
            ? this.push(this.emphasisNode(found), found.end)
            : this.literal(char.repeat(runOf(this.text, this.at, char)))
    }

    /**
     * The node for emphasis the reader takes, its words read only then. Reading them for every opener the backward
     * pass looks at would read each nested span again for every opener around it, which doubles with every level.
     */
    private emphasisNode({ size, from, to }: Emphasis): MarkdownInline {
        const children = new InlineReader(this.text.slice(from, to), this.links, this.depth + 1).read()
        return size === 1
            ? { kind: 'em', children }
            : { kind: 'strong', children: size === 2 ? children : [{ kind: 'em', children }] }
    }

    /** The emphasis a run opens at `at`, closed by the first run that can close it; `***` opens bold italics. */
    private emphasisAt(at: number): Emphasis | null {
        const known = this.opened.get(at)
        if (known !== undefined) {
            return known
        }
        const char = this.text.charAt(at)
        const run = runOf(this.text, at, char)
        let found: Emphasis | null = null
        if (this.depth < DEEPEST_EMPHASIS && canOpen(this.text, at, run, char)) {
            found = (run >= 3 ? this.enclose(at, char, 3) : null) ?? this.enclose(at, char, Math.min(run, 2))
        }
        this.opened.set(at, found)
        return found
    }

    /** Emphasis of `size` delimiters (1 italic, 2 bold, 3 both) from `at`, when a run closes it: where it lies, only. */
    private enclose(at: number, char: string, size: number): Emphasis | null {
        const end = this.closer(at + size, char, size)
        return end < 0 ? null : { size, from: at + size, to: end, end: end + size }
    }

    /**
     * Where the emphasis opened before `from` closes: the first run of `char`, at least `size` long, that can close it.
     * Code spans are passed over whole, and so is emphasis opened inside it, so `*a **b** c*` closes at its last `*`.
     */
    private closer(from: number, char: string, size: number): number {
        let scan = from
        while (scan < this.text.length) {
            const here = this.text.charAt(scan)
            if (here === '*' || here === '_') {
                const run = runOf(this.text, scan, here)
                if (here === char && run >= size && scan > from && canClose(this.text, scan, run, here)) {
                    return scan
                }
                scan = (canOpen(this.text, scan, run, here) ? this.emphasisAt(scan)?.end : undefined) ?? scan + run
            } else {
                scan = this.past(scan)
            }
        }
        return -1
    }

    /** The index after the character at `scan`, or after the escape, code span or web address it starts. */
    private past(scan: number): number {
        const here = this.text.charAt(scan)
        if (here === '\\') {
            return scan + 2
        }
        const url = this.links ? bareUrlAt(this.text, scan) : null
        if (url) {
            return url.end
        }
        if (here === '`') {
            return codeSpan(this.text, scan)?.end ?? scan + runOf(this.text, scan, '`')
        }
        return scan + 1
    }
}
